// src/components/frontpage/GroupFrontPage.jsx
// Wrapper for the Group desktop front page (>=1200px) — the mauve-context sibling of
// LocalityFrontPage. Same single-row, 5-column geometry (July 2026 re-layout): GroupHome
// (fixed-width) · News · Events · Chat · Marketplace (fixed-equal), all full height.
//
// Differences from LocalityFrontPage (content, not geometry):
//   - Column 1 is GroupHomeColumn (group identity / sub-groups / members / part-of), not Directory.
//   - The feed columns are the GROUP variants (GroupChatColumn / GroupEventsColumn) so create +
//     reply scope to the group (community_id); News "+" also carries groupId/groupName.
//   - Column 5 is SwapColumn (Marketplace, coming-soon parity) instead of a Groups doorway —
//     there's no "browse other groups" affordance needed from inside a group already.
//
// Theme is mauve via the existing ThemeProvider (theme.primary); no theming work here.
// Mobile and Group discovery (no selectedGroup) continue to use the existing mode grid.

import React, { useState, useEffect } from 'react';
import GroupHomeColumn from './columns/GroupHomeColumn';
import GroupChatColumn from './columns/GroupChatColumn';
import GroupEventsColumn from './columns/GroupEventsColumn';
import SwapColumn from './columns/SwapColumn';
import ModeColumn from './columns/ModeColumn';
import MoreButton from './columns/MoreButton';
import NewsMiniGrid from './NewsMiniGrid';
import GroupClosingBanner from '../groups/GroupClosingBanner';
import { useTranslation } from '../../i18n/useTranslation';
import { useTheme } from '../../themes/ThemeProvider';
import { useAuth } from '../../contexts/AuthContext';
import { supabaseFetch } from '../../lib/supabase';
import { parseEventDate } from '../../utils/eventDateTime';
import useMediaQuery, { NEWS_DOUBLE_COLUMN_BREAKPOINT } from '../../hooks/useMediaQuery';

// Fixed rail width for GroupHomeColumn (left) — matches LocalityFrontPage's DIRECTORY_WIDTH.
const GROUP_HOME_WIDTH = '300px';

const GroupFrontPage = ({
  group,
  articles = [],
  newsHasMore = false,
  chats = [],
  events = [],
  onArticleClick,
  onChatClick,
  onEventClick,
  onModeSwitch,
  // Chat accordion props
  openChatThreadId = null,
  onChatThreadToggle,
  onChatsRefresh,
  // Group-home props
  currentUser = null,
  groupClosing = null,   // useGroupClosing state from MainFeed (Roadmap #103); null = not closing
  onGroupSelect,
  onMembershipChange,
  canWriteChat = false     // members only may write in a group's chat (RLS "Members write where they can read")
}) => {
  const { t } = useTranslation('frontpage');
  const { t: tArticles } = useTranslation('articles');
  const { windowWidth } = useMediaQuery();
  const theme = useTheme();
  const { requireAuth } = useAuth();

  // News "+" — same contract as HeaderButtonsFeed's triggerCreateAction 'news' case, group-scoped.
  const handleNewArticle = () => {
    requireAuth('gate_body_post', () => {
      window.dispatchEvent(new CustomEvent('ournet:navigate', {
        detail: { view: 'new-article', mode: 'add', groupId: group?.id, groupName: group?.name }
      }));
    });
  };

  // Event details (start_date, venue_name etc.) from event_details table
  const [eventDetails, setEventDetails] = useState({});

  // Fetch event_details for real events (start_date, venue_name etc.) — mirrors LocalityFrontPage
  useEffect(() => {
    if (!events || events.length === 0) {
      setEventDetails({});
      return;
    }

    let cancelled = false;
    const fetchEventDetails = async () => {
      const eventIds = events.map(e => e.id).filter(Boolean);
      if (eventIds.length === 0) return;

      const { data, error } = await supabaseFetch.query('event_details', {
        select: 'article_id,start_date,end_date,display_from,venue_name,venue_address',
        inFilter: { article_id: eventIds }
      });

      if (error) {
        console.error('Error fetching event_details:', error);
        return;
      }

      if (!cancelled && data) {
        const detailsMap = {};
        for (const d of data) {
          detailsMap[d.article_id] = d;
        }
        setEventDetails(detailsMap);
      }
    };

    fetchEventDetails();
    return () => { cancelled = true; };
  }, [events]);

  // --- Chat data: filter to originals, sorted by most recent activity ---
  // Exclude orphan gravestones: soft-deleted roots with zero live replies
  const chatData = (chats || [])
    .filter(c => !c.parent_chat_id && !(c.is_deleted_by_author && (c.reply_count || 0) === 0))
    .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));

  // --- Event data: merge event_details, filter to the display window, sort soonest first ---
  const eventData = (events || [])
    .filter(e => e.article_type === 'event')
    .map(e => {
      const details = eventDetails[e.id];
      return {
        ...e,
        start_date: details?.start_date || null,
        end_date: details?.end_date || null,
        display_from: details?.display_from || null,
        venue_name: details?.venue_name || null,
        venue_address: details?.venue_address || null
      };
    })
    // Full roster — the Events column owns the Future/Past split (no feed-window pre-filter here).
    .sort((a, b) => parseEventDate(a.start_date) - parseEventDate(b.start_date));

  // Real news articles for the NewsBar (website-embed articles ride the news family; already
  // hoisted to the top by getGroupArticles)
  const newsArticles = (articles || []).filter(a => a.article_type === 'news' || a.article_type === 'website');

  // Same viewport-responsive News:Events ratio as LocalityFrontPage — see
  // NEWS_DOUBLE_COLUMN_BREAKPOINT's comment in useMediaQuery.js for the breakpoint arithmetic.
  const newsColumnWeight = windowWidth >= NEWS_DOUBLE_COLUMN_BREAKPOINT ? '2fr' : '1fr';

  return (
    // Single-row, 5-column grid — same geometry as LocalityFrontPage (July 2026 re-layout).
    // GroupHome is fixed-width (GROUP_HOME_WIDTH); News ratio is viewport-responsive (see
    // newsColumnWeight above); Events, Chat and Marketplace are equal 1fr, all full height.
    // A closing group (Roadmap #103) shows its notice in-flow above the columns; the grid takes the
    // remaining height.
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', minHeight: 0 }}>
    <GroupClosingBanner
      closing={!!groupClosing?.closing}
      closesOn={groupClosing?.closesOn}
      variant="group"
      canUndo={!!groupClosing?.canUndo}
      undoing={!!groupClosing?.undoing}
      undoFailed={!!groupClosing?.undoFailed}
      onUndo={groupClosing?.undo}
    />
    <div style={{
      display: 'grid',
      gridTemplateColumns: `${GROUP_HOME_WIDTH} minmax(0, ${newsColumnWeight}) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)`,
      columnGap: '12px',
      flex: 1,
      width: '100%',
      minHeight: 0,
      overflow: 'hidden',
      padding: '2px 12px 0 12px',
      boxSizing: 'border-box'
    }}>
      {/* Group-home — full-height left rail, fixed width */}
      <div style={{ minHeight: 0, minWidth: 0, overflow: 'hidden' }}>
        <GroupHomeColumn
          group={group}
          currentUser={currentUser}
          onGroupSelect={onGroupSelect}
          onMembershipChange={onMembershipChange}
        />
      </div>

      {/* News — its own full-height column, header matches Events/Chat/Marketplace (title +
          "+" to write a new article, group-scoped) */}
      <div style={{ minHeight: 0, minWidth: 0, overflow: 'hidden' }}>
        <ModeColumn
          title={t('news')}
          themeColor={theme.primary}
          onAdd={groupClosing?.closing ? undefined : handleNewArticle}
          addLabel={tArticles('new_article')}
          scrollBody={false}
          footer={
            <MoreButton
              hasMore={newsHasMore}
              onClick={() => onModeSwitch?.('news')}
              label={t('news_more')}
              color={theme.primary}
            />
          }
        >
          <div style={{ flex: 1, minHeight: 0 }}>
            <NewsMiniGrid articles={newsArticles} onArticleClick={onArticleClick} />
          </div>
        </ModeColumn>
      </div>

      {/* Events — its own full-height column; group-scoped create (new-event carries groupId) */}
      <div style={{ minHeight: 0, minWidth: 0, overflow: 'hidden' }}>
        <GroupEventsColumn
          data={eventData}
          onClick={onEventClick}
          group={group}
          canCreate={!groupClosing?.closing}
        />
      </div>

      {/* Chat — its own full-height column; group-scoped (create + reply carry community_id) */}
      <div style={{ minHeight: 0, minWidth: 0, overflow: 'hidden' }}>
        <GroupChatColumn
          data={chatData}
          group={group}
          openThreadId={openChatThreadId}
          onThreadToggle={onChatThreadToggle}
          onChatsRefresh={onChatsRefresh}
          canWrite={canWriteChat}
        />
      </div>

      {/* Marketplace — full-height right rail. Coming-soon parity with Locality (Tier 3 enables
          per group). */}
      <div style={{ minHeight: 0, minWidth: 0, overflow: 'hidden' }}>
        <SwapColumn data={[]} comingSoon={true} />
      </div>
    </div>
    </div>
  );
};

export default GroupFrontPage;
